import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import ExplicitBlur from "../components/ExplicitBlur";
import { useSettings } from "../context/SettingsContext";

export default function Home() {
    const [prints, setPrints] = useState([]);
    const { settings } = useSettings();

    useEffect(() => {
        api.get("/prints").then((r) => setPrints(r.data.slice(0, 4))).catch(() => {});
    }, []);

    return (
        <div className="relative z-10" data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[100svh] flex items-end pb-16 sm:pb-24 pt-28 sm:pt-32 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://customer-assets.emergentagent.com/job_studio-canvas-16/artifacts/lmrdnrml_Untitled_Artwork%203.webp"
                        alt="Original portrait by Nick Moore"
                        className="w-full h-full object-cover object-right saturate-[.7]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#FAFAF7] via-[#FAFAF7]/80 to-[#FAFAF7]/25" />
                    <div className="absolute inset-0 bg-[#FAFAF7]/15" />
                </div>
                <div className="relative max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 w-full fade-up">
                    <div className="label-caps mb-6 text-[#8C3A3A]">{settings.hero_kicker}</div>
                        <h1 className="font-hero-thin text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
                        {settings.hero_headline}<br />
                        <span className="block mt-1 text-xl sm:text-2xl lg:text-3xl leading-relaxed tracking-wide text-[#1A1A1A]" style={{ fontWeight: 300 }}>{settings.hero_subline}</span>
                    </h1>
                    <div className="mt-4 label-caps">Works by Nick Moore</div>
                    <p className="mt-8 text-[#5A5A5A] max-w-xl text-base leading-relaxed">
                        {settings.hero_description}
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
                        <Link
                            to="/gallery"
                            className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#FAFAF7] px-8 py-4 rounded-full font-medium text-sm tracking-wide hover:bg-[#8C3A3A] hover:text-white transition-colors"
                            data-testid="hero-view-gallery-btn"
                        >
                            View Gallery
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ transitionProperty: "transform" }} />
                        </Link>
                        <Link to="/commissions" className="text-sm text-[#5A5A5A] hover:text-[#1A1A1A] tracking-wide nav-link" data-testid="hero-commissions-link">
                            Request a commission
                        </Link>
                    </div>
                </div>
            </section>

            {/* FEATURED */}
            <section className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-24" data-testid="featured-section">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                    <div>
                        <div className="label-caps text-[#8C3A3A] mb-4">Selected Works</div>
                        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] tracking-tighter font-light">
                            Recently added to the archive
                        </h2>
                    </div>
                    <Link to="/gallery" className="text-sm text-[#5A5A5A] hover:text-[#1A1A1A] nav-link tracking-wide">
                        See full gallery →
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {prints.map((p, idx) => (
                        <Link
                            key={p.id}
                            to={`/print/${p.slug}`}
                            className="block group"
                            data-testid={`featured-print-${idx}`}
                        >
                            <div className="aspect-[3/4] mb-4 overflow-hidden bg-[#F0EDE5]">
                                <ExplicitBlur
                                    isExplicit={p.is_explicit}
                                    src={fileUrl(p.image_url || p.image_path)}
                                    alt={p.title}
                                    className="w-full h-full transition-transform duration-700 group-hover:scale-105"
                                    testId={`featured-reveal-${idx}`}
                                />
                            </div>
                            <div className="label-caps mb-2">{p.medium}</div>
                            <div className="font-serif-display text-xl text-[#1A1A1A] mb-1">{p.title}</div>
                            <div className="text-sm text-[#5A5A5A]">From A${p.price.toFixed(0)}</div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* MANIFESTO STRIP */}
            <section className="border-y border-black/[0.06] py-24 bg-[#F0EDE5]/50 relative z-10">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <div className="label-caps text-[#8C3A3A] mb-6">The Practice</div>
                    <p className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-[#1A1A1A] font-light leading-tight tracking-tight">
                        A solo practice devoted to the portraiture of the male form — charcoal, coloured pencil, and paint. Each work unfolds slowly, over days and weeks, through a distinctly queer perspective on beauty, strength, and intimacy.
                    </p>
                    <div className="mt-8 label-caps">Nicholas Moore — Artist, Australia</div>
                </div>
            </section>

            {/* INSTAGRAM FEED */}
            <section className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-24 relative z-10" data-testid="home-behold-feed">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="label-caps text-[#8C3A3A] mb-3">Portfolio Feed</div>
                        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] font-light tracking-tighter">
                            The latest from the studio.
                        </h2>
                    </div>
                    <a
                        href="https://instagram.com/art_nmoore"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-[#5A5A5A] hover:text-[#1A1A1A] nav-link tracking-wide"
                        data-testid="home-portfolio-link"
                    >
                        <Instagram className="w-4 h-4" /> @art_nmoore →
                    </a>
                </div>
                <div data-behold-id="4IRijCLi2bu4kTEc6k2f"></div>
            </section>
        </div>
    );
}
